import {
  FeaturedBadgeRule,
  FeaturedBadgeRuleCreatePayload,
  FeaturedBadgeRuleUpdatePayload,
  FeaturedBadgeRuleGroupDeletePayload,
  FeaturedBadgeRuleGroupTogglePayload,
  FeaturedBadgeRuleEntriesDeletePayload,
} from "../../models/rulesFeaturedBadge";
import { API_ENDPOINTS } from "../../utils/api-service";
import { api } from "./api";

const featuredBadgeRuleTag = (id: number) => ({
  type: "rulesFeaturedBadge" as const,
  id,
});

const featuredBadgeRuleListTag = { type: "rulesFeaturedBadge" as const, id: "LIST" };

const unwrapApiData = (response: unknown): unknown => {
  if (typeof response !== "object" || response === null) {
    return response;
  }

  const record = response as Record<string, unknown>;
  if (record.success === true && record.data !== undefined) {
    return record.data;
  }

  return response;
};

const toBoolean = (value: unknown): boolean => {
  if (value === false || value === 0 || value === "0" || value === "false") {
    return false;
  }

  return value === true || value === 1 || value === "1" || value === "true";
};

const toInt = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
};

const toFloat = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toThresholdMinutes = (row: Record<string, unknown>): number => {
  const rawMinutes = row.thresholdMinutes ?? row.threshold_minutes;
  if (rawMinutes != null) {
    return toInt(rawMinutes);
  }

  const legacyHours = row.thresholdHours ?? row.threshold_hours;
  if (legacyHours != null) {
    return Math.round(toFloat(legacyHours) * 60);
  }

  return 0;
};

const mapFeaturedBadgeRule = (raw: unknown): FeaturedBadgeRule => {
  const row = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;

  return {
    id: toInt(row.id),
    tenantId:
      row.tenantId == null && row.tenant_id == null
        ? null
        : String(row.tenantId ?? row.tenant_id),
    tenantName:
      row.tenantName == null && row.tenant_name == null
        ? undefined
        : String(row.tenantName ?? row.tenant_name ?? "").trim() || undefined,
    eventTypeId:
      row.eventTypeId == null && row.event_type_id == null
        ? null
        : toInt(row.eventTypeId ?? row.event_type_id),
    eventTypeName:
      row.eventTypeName == null && row.event_type_name == null
        ? undefined
        : String(row.eventTypeName ?? row.event_type_name ?? "").trim() || undefined,
    eventSubTypeId:
      row.eventSubTypeId == null && row.event_sub_type_id == null
        ? null
        : toInt(row.eventSubTypeId ?? row.event_sub_type_id),
    eventSubTypeName:
      row.eventSubTypeName == null && row.event_sub_type_name == null
        ? undefined
        : String(row.eventSubTypeName ?? row.event_sub_type_name ?? "").trim() || undefined,
    slug: String(row.slug ?? ""),
    label: String(row.label ?? ""),
    thresholdMinutes: toThresholdMinutes(row),
    colorVariant: toInt(row.colorVariant ?? row.color_variant, 1),
    active: toBoolean(row.active),
    createdAt:
      row.createdAt == null && row.created_at == null
        ? undefined
        : String(row.createdAt ?? row.created_at),
  };
};

const normalizeListResponse = (response: unknown): unknown[] => {
  const unwrapped = unwrapApiData(response);
  return Array.isArray(unwrapped) ? unwrapped : [];
};

const listProvidesTags = (result: FeaturedBadgeRule[] | undefined) =>
  result
    ? [...result.map(({ id }) => featuredBadgeRuleTag(id)), featuredBadgeRuleListTag]
    : [featuredBadgeRuleListTag];

type EbApiRootState = {
  ebAPI?: {
    queries?: Record<string, { endpointName?: string; originalArgs?: void }>;
  };
};

const patchFeaturedBadgeRulesListCaches = (
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
  updater: (draft: FeaturedBadgeRule[]) => void
): Array<{ undo: () => void }> => {
  const patchResults: Array<{ undo: () => void }> = [];
  const rootState = getState() as EbApiRootState;

  for (const query of Object.values(rootState.ebAPI?.queries ?? {})) {
    if (query?.endpointName !== "getFeaturedBadgeRules") {
      continue;
    }

    const cachedList = rulesFeaturedBadgeApi.endpoints.getFeaturedBadgeRules.select()(
      rootState as never
    );
    if (!cachedList.data) {
      continue;
    }

    const patch = dispatch(
      rulesFeaturedBadgeApi.util.updateQueryData("getFeaturedBadgeRules", undefined, updater)
    ) as { undo?: () => void };

    if (patch?.undo) {
      patchResults.push({ undo: patch.undo });
    }
  }

  return patchResults;
};

const toRuleBody = (body: FeaturedBadgeRuleCreatePayload) => ({
  tenantId: body.tenantId,
  eventTypeId: body.eventTypeId,
  eventSubTypeId: body.eventSubTypeId,
  slug: String(body.slug).trim(),
  label: String(body.label).trim(),
  thresholdMinutes: Number(body.thresholdMinutes),
  colorVariant: Number(body.colorVariant),
  active: toBoolean(body.active),
});

const toGroupScopeBody = (body: FeaturedBadgeRuleGroupDeletePayload) => ({
  tenantId: body.tenantId,
  eventTypeId: body.eventTypeId,
  eventSubTypeId: body.eventSubTypeId,
});

const toGroupToggleBody = (body: FeaturedBadgeRuleGroupTogglePayload) => ({
  ...toGroupScopeBody(body),
  active: toBoolean(body.active),
});

const matchesGroupScope = (
  row: FeaturedBadgeRule,
  scope: FeaturedBadgeRuleGroupDeletePayload
): boolean =>
  (row.tenantId ?? null) === (scope.tenantId ?? null) &&
  (row.eventTypeId ?? null) === (scope.eventTypeId ?? null) &&
  (row.eventSubTypeId ?? null) === (scope.eventSubTypeId ?? null);

export const rulesFeaturedBadgeApi = api.injectEndpoints({
  endpoints: (build) => ({
    getFeaturedBadgeRules: build.query<FeaturedBadgeRule[], void>({
      query: () => ({
        url: API_ENDPOINTS.RULES.FEATURED_BADGE.BASE,
      }),
      transformResponse: (response: unknown) =>
        normalizeListResponse(response).map(mapFeaturedBadgeRule),
      providesTags: (result) => listProvidesTags(result),
    }),

    createFeaturedBadgeRules: build.mutation<
      FeaturedBadgeRule[],
      { entries: FeaturedBadgeRuleCreatePayload[] }
    >({
      query: ({ entries }) => ({
        url: API_ENDPOINTS.RULES.FEATURED_BADGE.CREATE,
        method: "POST",
        body: { entries: entries.map((entry) => toRuleBody(entry)) },
      }),
      transformResponse: (response: unknown) =>
        normalizeListResponse(response).map(mapFeaturedBadgeRule),
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          patchFeaturedBadgeRulesListCaches(dispatch, getState, (draft) => {
            draft.push(...data);
          });
        } catch {
          // Sin invalidación: el submit agrupa mutaciones y el cache se parchea acá.
        }
      },
      invalidatesTags: [],
    }),

    updateFeaturedBadgeRules: build.mutation<
      FeaturedBadgeRule[],
      { entries: FeaturedBadgeRuleUpdatePayload[] }
    >({
      query: ({ entries }) => ({
        url: API_ENDPOINTS.RULES.FEATURED_BADGE.UPDATE,
        method: "PUT",
        body: {
          entries: entries.map((entry) => ({
            id: toInt(entry.id),
            ...toRuleBody(entry),
          })),
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeListResponse(response).map(mapFeaturedBadgeRule),
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          patchFeaturedBadgeRulesListCaches(dispatch, getState, (draft) => {
            for (const row of data) {
              const existing = draft.find((item) => item.id === row.id);
              if (existing) {
                Object.assign(existing, row);
              }
            }
          });
        } catch {
          // Sin invalidación: el submit agrupa mutaciones y el cache se parchea acá.
        }
      },
      invalidatesTags: [],
    }),

    toggleFeaturedBadgeRules: build.mutation<
      FeaturedBadgeRule[],
      FeaturedBadgeRuleGroupTogglePayload
    >({
      query: (body) => ({
        url: API_ENDPOINTS.RULES.FEATURED_BADGE.TOGGLE,
        method: "PATCH",
        body: toGroupToggleBody(body),
      }),
      transformResponse: (response: unknown) =>
        normalizeListResponse(response).map(mapFeaturedBadgeRule),
      async onQueryStarted({ active, ...scope }, { dispatch, queryFulfilled, getState }) {
        const optimisticPatches = patchFeaturedBadgeRulesListCaches(dispatch, getState, (draft) => {
          for (const row of draft) {
            if (matchesGroupScope(row, scope)) {
              row.active = active;
            }
          }
        });

        try {
          const { data } = await queryFulfilled;
          patchFeaturedBadgeRulesListCaches(dispatch, getState, (draft) => {
            for (const row of data) {
              const existing = draft.find((item) => item.id === row.id);
              if (existing) {
                Object.assign(existing, row);
              }
            }
          });
        } catch {
          optimisticPatches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: [],
    }),

    deleteFeaturedBadgeRulesEntries: build.mutation<
      { deletedCount: number },
      FeaturedBadgeRuleEntriesDeletePayload
    >({
      query: (body) => ({
        url: API_ENDPOINTS.RULES.FEATURED_BADGE.DELETE_ENTRIES,
        method: "DELETE",
        body,
      }),
      transformResponse: (response: unknown) => {
        const unwrapped = unwrapApiData(response);
        const record =
          typeof unwrapped === "object" && unwrapped !== null
            ? (unwrapped as Record<string, unknown>)
            : {};
        return {
          deletedCount: toInt(record.deletedCount),
        };
      },
      async onQueryStarted({ ids }, { dispatch, queryFulfilled, getState }) {
        const idSet = new Set(ids);
        const optimisticPatches = patchFeaturedBadgeRulesListCaches(dispatch, getState, (draft) =>
          draft.filter((row) => !idSet.has(row.id))
        );

        try {
          const { data } = await queryFulfilled;
          if (data.deletedCount === 0) {
            optimisticPatches.forEach((patch) => patch.undo());
          }
        } catch {
          optimisticPatches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: [],
    }),

    deleteFeaturedBadgeRuleGroup: build.mutation<
      { deletedCount: number },
      FeaturedBadgeRuleGroupDeletePayload
    >({
      query: (body) => ({
        url: API_ENDPOINTS.RULES.FEATURED_BADGE.DELETE,
        method: "DELETE",
        body: toGroupScopeBody(body),
      }),
      transformResponse: (response: unknown) => {
        const unwrapped = unwrapApiData(response);
        const record =
          typeof unwrapped === "object" && unwrapped !== null
            ? (unwrapped as Record<string, unknown>)
            : {};
        return {
          deletedCount: toInt(record.deletedCount),
        };
      },
      async onQueryStarted(scope, { dispatch, queryFulfilled, getState }) {
        const optimisticPatches = patchFeaturedBadgeRulesListCaches(dispatch, getState, (draft) =>
          draft.filter((row) => !matchesGroupScope(row, scope))
        );

        try {
          const { data } = await queryFulfilled;
          if (data.deletedCount === 0) {
            optimisticPatches.forEach((patch) => patch.undo());
          }
        } catch {
          optimisticPatches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: [],
    }),
  }),
});

export const {
  useGetFeaturedBadgeRulesQuery,
  useCreateFeaturedBadgeRulesMutation,
  useUpdateFeaturedBadgeRulesMutation,
  useToggleFeaturedBadgeRulesMutation,
  useDeleteFeaturedBadgeRulesEntriesMutation,
  useDeleteFeaturedBadgeRuleGroupMutation,
} = rulesFeaturedBadgeApi;
